/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-06 18:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { Modal, TextStyle, Button, TextField } from '@shopify/polaris';
import { MobileCancelMajor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import {
  formatPriceDisplay,
  buildDiscountInvoice,
  getDiscountCodeValue
} from 'src/helpers/subscription-v3-helper';
import { DISCOUNT_ERRORS, ADDONS_INFO, ANALYTICS_ADDON } from 'src/constants/subscription';
import TermsConditionsModal from 'src/components/views/subscription-v3/terms-conditions-modal';
// DONE
const CostBreakdownModal = props => {
  const {
    active,
    i18n,
    packageInfo,
    handleCancel,
    planInfo = {},
    status,
    tenantApp,
    host,
    verifyDiscountCode,
    verifying,
    remainCycles,
    invoice,
    subscribe,
    msgVerifyError,
    discountVerified,
    clearDiscountCode,
    termsConditions
  } = props;

  // state
  const [currentInvoiceData, setCurrentInvoiceData] = useState({});
  const [acceptedTC, setAcceptedTC] = useState(false);
  const [code, setCode] = useState(null);
  const [showDiscountCodeInput, setShowDiscountCodeInput] = useState(null);
  const [activeTermsConditionsModal, setActiveTermsConditionsModal] = useState(false);

  // Build data
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const hasBilling =
    !['trial', 'expiredSoon', 'expired'].includes(status) && !_.isEmpty(selectedPlan);
  const {
    addons = [],
    currentPackage,
    discountInfo = {},
    amountDiscount = 0,
    totalPrice = 0
  } = currentInvoiceData;

  // Expired date of discount duration
  const termsConditionsContent = _.get(termsConditions, 'content', '');

  // Clear discount code has scope = addon when unsubscribe all add-on
  const { addon = [] } = planInfo;
  const addonNames = addon.map(e => e.addOnName);

  // handle subscribe package (addon + discount)
  const handleSubscribe = async () => {
    const planId = _.get(planInfo, 'name', null);
    // Plan id is not valid
    if (!planId) {
      return;
    }
    // Remove spaces
    const codeFormat = code.replace(/\s/g, '');
    await subscribe(planId, codeFormat, host, addonNames);
  };

  // Clear discount code
  const handleClearDiscountCode = async () => {
    await clearDiscountCode();
    setShowDiscountCodeInput(false);
    setCode(null);
    setAcceptedTC(false);
  };

  // Handle process data to state when accepted from Terms & Conditions modal
  const handleAcceptTC = () => {
    setAcceptedTC(true);
    setActiveTermsConditionsModal(false);
  };

  // Handle verify discount code
  const verify = () => {
    const name = _.get(planInfo, 'name', '');
    verifyDiscountCode(code, 'subscription-page', addonNames, name);
  };

  // Handle cancel
  const closeModal = () => {
    handleClearDiscountCode();
    handleCancel();
  };

  // useEffect
  useEffect(() => {
    const currentInvoiceData = buildDiscountInvoice(planInfo, packageInfo, discountVerified, i18n);
    setCurrentInvoiceData(currentInvoiceData);
  }, [planInfo]);

  useEffect(() => {
    if (hasBilling || (!hasBilling && acceptedTC)) {
      const currentInvoiceData = buildDiscountInvoice(
        planInfo,
        packageInfo,
        discountVerified,
        i18n
      );
      setCurrentInvoiceData(currentInvoiceData);
    }
  }, [discountVerified, acceptedTC, addon]);

  useEffect(() => {
    const { dcRawValue } = getDiscountCodeValue(discountVerified);
    if (!_.isEmpty(discountVerified) && dcRawValue > 0 && code) {
      setActiveTermsConditionsModal(true);
    }
  }, [discountVerified]);

  const { dcRawValue } = getDiscountCodeValue(discountVerified);
  const duration = _.get(invoice, 'discountCodeApplied.duration', 0);
  const discountDuration = remainCycles || duration || 0;

  return (
    <Modal
      open={active}
      onClose={closeModal}
      title={i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.costBreakDown')}>
      <Modal.Section>
        <div className="cost-breakdown-modal">
          <div>
            {/* Render package infos */}
            {packageInfo &&
              renderCostComponent(i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.package'), [
                currentPackage
              ])}
            {/* ------------------- */}

            {/* Render list current addon + price */}
            {addons &&
              addons.length > 0 &&
              renderCostComponent(
                i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.addon'),
                addons
              )}
            {/* ----------discount info--------- */}

            {/* Is trial or subscribe with discount code */}
            {(!hasBilling || !_.isEmpty(discountInfo)) && (
              <div>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.discount')}
                </TextStyle>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '6px'
                  }}>
                  {/* subscribe with discount code */}
                  {hasBilling && <div className="code">{_.get(discountInfo, 'code', '')}</div>}
                  {/* For trial user => not has billing */}
                  {acceptedTC && (
                    <div className="code">
                      {_.get(discountInfo, 'code', '')}
                      <Button onClick={handleClearDiscountCode} icon={MobileCancelMajor} plain />
                    </div>
                  )}
                  {/* trial user => need to subscribe with discount code  */}
                  {!hasBilling &&
                    !acceptedTC &&
                    (showDiscountCodeInput ? (
                      <div>
                        <div className="discountCode">
                          <TextField
                            value={code}
                            onChange={value => setCode(value)}
                            placeholder={i18n.translate(
                              'AdminHTML.SubscriptionV3.Common.discountCodePlaceHolder'
                            )}
                            connectedRight={
                              <Button
                                primary
                                onClick={verify}
                                loading={verifying}
                                disabled={code ? false : true}>
                                {i18n.translate('AdminHTML.SubscriptionV3.Actions.apply')}
                              </Button>
                            }
                          />
                        </div>
                        {/* Code is not empty or discountRawValue > 0*/}
                        {msgVerifyError && code && !dcRawValue && (
                          <div className={'applied error'}>
                            {DISCOUNT_ERRORS[msgVerifyError]
                              ? i18n.translate(DISCOUNT_ERRORS[msgVerifyError])
                              : msgVerifyError}
                          </div>
                        )}
                        <TermsConditionsModal
                          active={activeTermsConditionsModal}
                          handleChangeModal={() => setActiveTermsConditionsModal(false)}
                          content={termsConditionsContent}
                          subscribe={handleAcceptTC}
                        />
                      </div>
                    ) : (
                      // Render label add discount
                      <div
                        onClick={() => setShowDiscountCodeInput(true)}
                        style={{ color: '#2C6ECB', cursor: 'pointer' }}>
                        {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.addDiscount')}
                      </div>
                    ))}
                  {/* Price is discounted */}
                  <p style={{ color: '#008060' }}>${formatPriceDisplay(amountDiscount)}</p>
                </div>
              </div>
            )}
          </div>
          {/* Render discount box info */}
          {(!_.isEmpty(discountInfo) || acceptedTC) &&
            renderDiscount(currentInvoiceData, discountDuration, i18n)}
          {/* Render discount box preview before subscribe */}

          <div className="divider" />
          {/* Render total bill preview */}
          <div className="total-bill">
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.totalBill')}
            </TextStyle>
            <TextStyle variation="strong">${formatPriceDisplay(totalPrice)}</TextStyle>
          </div>
          {acceptedTC && (
            <div className="plan-status-action">
              <Button primary onClick={handleSubscribe}>
                {i18n.translate('AdminHTML.SubscriptionV3.Actions.confirmSubscribe')}
              </Button>
            </div>
          )}
        </div>
      </Modal.Section>
    </Modal>
  );
};

// Render discount component
const renderDiscount = (currentInvoiceData, discountDuration, i18n) => {
  const {
    addonsWithDiscount = {},
    discountInfo = {},
    packageWithDiscount = {},
    currentPackage = {},
    totalPrice = 0,
    originalPrice = 0
  } = currentInvoiceData;

  const { expiredAt = '' } = discountInfo;
  const { dcPostfixValue } = getDiscountCodeValue(discountInfo);

  return (
    <div className="discount-info">
      {discountInfo.discountType === 'percentage' && (
        <>
          <p>
            {discountDuration > 0
              ? i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.discountInfo_Label', {
                  value: dcPostfixValue,
                  duration: discountDuration
                })
              : i18n.translate(
                  'AdminHTML.SubscriptionV3.PlanStatus.discountInfo_LabelNotDuration',
                  {
                    value: dcPostfixValue
                  }
                )}
          </p>
          <ul>
            {/* Render package discounted info component */}
            {!_.isEmpty(packageWithDiscount) && (
              <li key={'package'}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <p>{currentPackage.name}</p>
                  <p style={{ color: '#008060' }}>
                    -${formatPriceDisplay(currentPackage.priceAfterDiscount)}
                  </p>
                </div>
              </li>
            )}
            {/* Render addon discounted info component (if have) */}
            {addonsWithDiscount.length > 0 &&
              addonsWithDiscount.map((addon, index) => {
                let findAddonDiscount = ADDONS_INFO.find(e => e.addonName === addon.name)?.title;
                if (_.includes(addon.name, 'analytics')) {
                  findAddonDiscount = ANALYTICS_ADDON.find(e => e.name === addon.name)?.label;
                }
                return (
                  <li key={index}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <p>{i18n.translate(findAddonDiscount)}</p>
                      <p style={{ color: '#008060' }}>
                        -${formatPriceDisplay(addon.discountPrice)}
                      </p>
                    </div>
                  </li>
                );
              })}
          </ul>
        </>
      )}
      {/* Render info discounted applied*/}
      {discountDuration > 0 && (
        <p>
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.discountInfo_Detail', {
            totalPrice: <strong>${formatPriceDisplay(totalPrice)}</strong>,
            duration: (
              <TextStyle variation="strong">
                {i18n.translate(
                  `AdminHTML.SubscriptionV3.PlanStatus.${
                    discountDuration > 1 ? 'discountDurationPlural' : 'discountDuration'
                  }`,
                  {
                    nt_duration: discountDuration
                  }
                )}
              </TextStyle>
            ),
            expiredAt: <strong>{expiredAt}</strong>,
            originalPrice: (
              <strong>
                {i18n.translate('AdminHTML.SubscriptionV3.Common.pricePerMonth', {
                  price: formatPriceDisplay(originalPrice)
                })}
              </strong>
            )
          })}
        </p>
      )}
    </div>
  );
};

// Render info cost with name package/addon + price
const renderCostComponent = (title, featureList) => {
  return (
    <div>
      <TextStyle variation="strong">{title}</TextStyle>
      <ul>
        {featureList.map(
          (e, index) =>
            e && (
              <li key={index}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <p>{e.name}</p>
                  <p>${formatPriceDisplay(e.price)}</p>
                </div>
              </li>
            )
        )}
      </ul>
    </div>
  );
};

export default withI18n()(CostBreakdownModal);
